'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface PartnerModalProps {
  partner: {
    id: number;
    name: string;
    type: string;
    logo: string;
    logoAlt: string;
    description: string;
    country: string;
    established: string;
    website: string;
    impact: string;
    fullDescription: string;
    achievements: string[];
    contactEmail: string;
    focusAreas: string[];
  } | null;
  onClose: () => void;
}

const PartnerModal: React.FC<PartnerModalProps> = ({ partner, onClose }) => {
  if (!partner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-headline font-bold text-foreground">
            Partner Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors duration-200"
          >
            <Icon name="XMarkIcon" size={24} className="text-foreground" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-6 mb-6">
            <div className="w-32 h-32 bg-background rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
              <AppImage
                src={partner.logo}
                alt={partner.logoAlt}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-2xl font-headline font-bold text-foreground">
                  {partner.name}
                </h3>
                <span className="px-3 py-1 bg-secondary/10 text-secondary text-sm font-semibold rounded-full">
                  {partner.type}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-foreground">
                  <Icon name="MapPinIcon" size={16} className="mr-2 text-primary" />
                  <span>{partner.country}</span>
                </div>
                <div className="flex items-center text-sm text-foreground">
                  <Icon name="CalendarIcon" size={16} className="mr-2 text-primary" />
                  <span>Established {partner.established}</span>
                </div>
                <div className="flex items-center text-sm text-foreground">
                  <Icon name="EnvelopeIcon" size={16} className="mr-2 text-primary" />
                  <a
                    href={`mailto:${partner.contactEmail}`}
                    className="text-primary hover:text-secondary transition-colors duration-200"
                  >
                    {partner.contactEmail}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-headline font-bold text-foreground mb-3">
              About
            </h4>
            <p className="text-foreground leading-relaxed">{partner.fullDescription}</p>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-headline font-bold text-foreground mb-3">
              Focus Areas
            </h4>
            <div className="flex flex-wrap gap-2">
              {partner.focusAreas.map((area, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-headline font-bold text-foreground mb-3">
              Key Achievements
            </h4>
            <ul className="space-y-2">
              {partner.achievements.map((achievement, index) => (
                <li key={index} className="flex items-start">
                  <Icon
                    name="CheckCircleIcon"
                    size={20}
                    className="mr-2 text-success flex-shrink-0 mt-0.5"
                  />
                  <span className="text-foreground">{achievement}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-muted rounded-lg p-4 mb-6">
            <div className="flex items-center mb-2">
              <Icon name="ChartBarIcon" size={20} className="mr-2 text-primary" />
              <h4 className="text-lg font-headline font-bold text-foreground">
                Partnership Impact
              </h4>
            </div>
            <p className="text-foreground">{partner.impact}</p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <a
              href={partner.website}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-secondary transition-colors duration-200 flex items-center"
            >
              <Icon name="GlobeAltIcon" size={20} className="mr-2" />
              Visit Website
            </a>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-muted text-foreground rounded-md font-semibold hover:bg-border transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerModal;